/*
 * Weather history charts. Two stacked sections inside the modal:
 *   1) Daily temperature bar chart — one bar per day in the month,
 *      height proportional to the temperature, colored by band.
 *   2) Per-kind tally — eight columns (one per weather kind), each
 *      with rectangles stacked from the bottom up. One rectangle =
 *      one day that kind was picked. Kids count the rectangles.
 *
 * Designed for K-2: nothing is required, just visual counting.
 */

.charts {
  display: flex;
  flex-direction: column;
  /* Tight gap so both charts fit without scrolling. */
  gap: clamp(12px, 1.4vw, 22px);
}

.section {
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.8vw, 12px);
}

.sectionHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.sectionTitle {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(18px, 1.5vw, 24px);
  color: var(--token-text-primary);
}

.empty {
  margin: 0;
  font-size: 14px;
  font-style: italic;
  color: rgba(28, 38, 32, 0.55);
}

/* ---------- Temperature chart ---------- */

.tempChart {
  background: rgba(255, 255, 255, 0.4);
  border: 1px dashed rgba(8, 18, 12, 0.15);
  border-radius: 14px;
  padding: 16px 18px 12px;
}

/* Two columns: left y-axis labels + right bar area. Both share the
 * same height so the y-axis labels line up with bar values. */
.tempPlot {
  display: grid;
  grid-template-columns: minmax(56px, auto) 1fr;
  gap: 12px;
  align-items: stretch;
}

/* Y-axis container. Holds 4–5 absolute-positioned labels keyed to the
 * temperature value (in °F under the hood). The container height
 * exactly matches the bar-wrap height so a "warm" label at 75°F sits
 * 75/maxTemp of the way up the left edge. */
.yAxis {
  position: relative;
  /* Compact so the kind-tally below fits the rest of the viewport
   * without scrolling. */
  height: clamp(120px, 16vh, 180px);
  border-right: 1px dashed rgba(8, 18, 12, 0.18);
  /* Pad-right so labels don't kiss the dashed divider line. */
  padding-right: 8px;
  font-family: var(--token-font-ui);
  font-size: clamp(12px, 1vw, 16px);
  font-weight: 700;
  letter-spacing: 0.3px;
  color: rgba(28, 38, 32, 0.7);
}

.yLabel {
  position: absolute;
  right: 8px;
  /* Pull the label up by half its line-height so it visually centres
   * on the value rather than starting at it. */
  transform: translateY(50%);
  white-space: nowrap;
  text-align: right;
}

.tempBars {
  display: grid;
  /* One column per day of the month. */
  grid-template-columns: repeat(31, 1fr);
  gap: 4px;
  align-items: stretch;
}

.tempCol {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.tempBarWrap {
  position: relative;
  width: 100%;
  /* Matches the y-axis container so the bar tops + labels align. */
  height: clamp(120px, 16vh, 180px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.tempBar {
  width: 80%;
  border-radius: 4px 4px 0 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
  /* Soft entry animation so the bars pop up when the modal opens. */
  animation: tempBarGrow 280ms cubic-bezier(0.22, 0.9, 0.3, 1) both;
  transform-origin: bottom center;
}

@keyframes tempBarGrow {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}

.tempValue {
  position: absolute;
  top: -4px;
  transform: translateY(-100%);
  font-family: var(--token-font-ui);
  font-size: 10px;
  font-weight: 700;
  color: rgba(28, 38, 32, 0.75);
  white-space: nowrap;
}

.tempDay {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 600;
  color: rgba(28, 38, 32, 0.55);
  /* Tabular numerals so the day labels align perfectly under bars. */
  font-variant-numeric: tabular-nums;
}

/* ---------- Per-kind tally ---------- */

.kindChart {
  display: grid;
  /* Eight uniform columns — gives plenty of width per kind for the
   * rectangle stack + icon + label. */
  grid-template-columns: repeat(8, 1fr);
  gap: clamp(8px, 0.8vw, 14px);
  background: rgba(255, 255, 255, 0.4);
  border: 1px dashed rgba(8, 18, 12, 0.15);
  border-radius: 14px;
  padding: 14px 12px;
  align-items: end;
}

.kindCol {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* Stack of rectangles. Reverse flex direction so new entries push
 * upward from the bottom (the "tower grows up" reading order). The
 * max-height + overflow gives a soft scrolling fallback if a column
 * grows past a full month's worth (unlikely in practice). */
.kindStack {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 3px;
  width: 100%;
  /* Min just for visual presence on empty columns — not so big that
   * an empty kind chart pushes the icons off-screen. The column
   * grows past this when there are more picks than fit. */
  min-height: clamp(180px, 24vh, 300px);
  padding: 2px 0;
}

.kindRect {
  width: 80%;
  max-width: 60px;
  /* Slightly shorter so 20+ rectangles can stack inside the column
   * without overflowing. */
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(8, 18, 12, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
  flex-shrink: 0;
  /* Pop-in animation so the rectangles read as building up. */
  animation: kindRectIn 220ms cubic-bezier(0.22, 0.9, 0.3, 1.1) both;
}

@keyframes kindRectIn {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.kindCount {
  font-family: var(--token-font-display);
  font-size: clamp(20px, 1.8vw, 28px);
  font-weight: 700;
  color: var(--token-text-primary);
  line-height: 1;
}

.kindIcon {
  width: clamp(36px, 4vw, 56px);
  height: clamp(36px, 4vw, 56px);
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.kindLabel {
  font-family: var(--token-font-ui);
  font-size: clamp(11px, 0.9vw, 13px);
  font-weight: 600;
  color: rgba(28, 38, 32, 0.7);
  text-align: center;
}
